import { useHistory } from 'react-router-dom';
import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FormProvider, useForm } from 'react-hook-form';
import { FormContainer } from 'react-hook-form-mui';
import * as yup from 'yup';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import LinearProgress from '@mui/material/LinearProgress';
import CloseIcon from '@mui/icons-material/Close';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { FeatureFlag } from '@cbo/shared-library';
import {
  dialogTitleSx,
  dialogActionsSx,
  dialogActionsButtonsSx,
  createDialogSx,
  createButtonSx,
  linearProgressSx,
  closeButtonSx,
  loadingSx,
  contentTextSx,
} from '../styles';
import { editMenuTitleSx } from '../../CustomReports/styles';
import routes from '../../../constants/routes';
import useYupValidationResolver from '../../../utils/formUtils/yupValidationResolver';
import SelectDataset from './SelectDataset';
import { useGetCustomReportsQuery } from '../../requests/queries';
import UnsavedFormDataPrompt from '../../../components/UnsavedDataPrompt/UnsavedFormDataPrompt';
import { CustomReportData, DatagridElement } from '../../requests/index';
import { checkTitleExists, datasetNameMap } from '../utils/EditReportUtils';
import { useFeatureFlag } from '../../../utils/hooks/useFeatureFlag';

export interface DatasetColumn {
  datasetId: string;
  optionId: string;
  label: string;
  id: string;
  filterType: string | undefined;
}

interface CentralReportsFormType {
  title: string;
  description: string;
  selectedColumns: DatasetColumn[];
  sortBy: string;
  sortOrder: 'asc' | 'desc';
}

enum Progress {
  Step1 = 50,
  Step2 = 100,
}

/* Main dialog component for creating a new custom report */
function CreateReportDialog({
  open,
  addAdditionalMode,
  handleClose: closeDialog,
}: Readonly<{
  open: boolean;
  addAdditionalMode?: boolean;
  handleClose: (newSection?: DatagridElement) => void;
}>) {
  const { t } = useTranslation();
  const history = useHistory();
  // Tracks which step of the dialog is active
  const [progress, setProgress] = useState(Progress.Step1);
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const [unsavedPromptOpen, setUnsavedPromptOpen] = useState(false);
  // Ref to bypass unsaved prompt when navigation is intentional (Step 2 Next)
  const bypassNavigationRef = useRef(false);
  const { data: allReports, isLoading: isAllReportsLoading } = useGetCustomReportsQuery(true);
  const cboCustomMetrics = useFeatureFlag(FeatureFlag.customMetrics);

  // React Hook Form setup with onChange validation for immediate feedback on title uniqueness
  const formContext = useForm<CentralReportsFormType>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      title: '',
      description: '',
      selectedColumns: [],
      sortBy: '',
      sortOrder: 'asc',
    },
    resolver: useYupValidationResolver(
      yup.object({
        title: yup
          .string()
          .required()
          .test('unique-title', t('admin.centralReportsPage.createReport.duplicateTitleError'), (value) => {
            if (!value || !allReports) return true;
            return checkTitleExists(value, allReports ?? [], null) === false;
          }),
        description: yup.string(),
        selectedColumns: yup.array(),
      })
    ),
  });

  const handleClose = (newSection?: DatagridElement) => {
    formContext.reset();
    closeDialog(newSection);
  };

  const hasUnsavedChanges = formContext.formState.isDirty || formContext.watch('selectedColumns').length > 0;

  const { register } = formContext;

  // Automatically set sortBy to first selected column when columns change
  const selectedColumns = formContext.watch('selectedColumns');
  useEffect(() => {
    const currentSortBy = formContext.getValues('sortBy');

    // If no columns selected, clear sortBy
    if (selectedColumns.length === 0) {
      formContext.setValue('sortBy', '', { shouldDirty: false });
      return;
    }

    // If sortBy is empty or the current sortBy column is no longer in selectedColumns, set to first column
    const sortByExists = selectedColumns.some((col) => col.label === currentSortBy);
    if (!currentSortBy || !sortByExists) {
      formContext.setValue('sortBy', selectedColumns[0].label, { shouldDirty: false });
    }
  }, [selectedColumns, formContext]);

  /* Helper to build initial CustomReport from form data */
  const buildInitReport = (): CustomReportData => {
    const title = formContext.getValues('title');
    const description = formContext.getValues('description');
    const selected = formContext.getValues('selectedColumns');
    const sortBy = formContext.getValues('sortBy');
    const sortOrder = formContext.getValues('sortOrder');
    const parentKey = datasetNameMap[selected[0]?.datasetId] || '';
    const selectedColumnNames = selected.map((c) => c.label);
    const selectedDataColumnNames = cboCustomMetrics
      ? selected.filter((c) => !c.optionId.startsWith('custommetric_')).map((c) => c.label)
      : selectedColumnNames;
    const customMetricIds = cboCustomMetrics
      ? selected
          .filter((c) => c.optionId.startsWith('custommetric_'))
          .map((c) => c.optionId.replace('custommetric_', ''))
      : [];

    return {
      title,
      description,
      category: 'Custom',
      detailsAndConfig: {
        data: {
          [parentKey]: {
            selectedColumns: selectedDataColumnNames,
            customMetricIds: customMetricIds.length > 0 ? customMetricIds : undefined,
          },
        },
        elements: [
          [
            {
              type: 'datagrid',
              columns: {
                [parentKey]: selectedColumnNames,
              },
              defaultSort: sortBy ? [{ field: sortBy, sortDirection: sortOrder }] : undefined,
            },
          ],
        ],
        additionalFilters: [],
      },
    };
  };

  const buildNewSection = () => {
    const selected = formContext.getValues('selectedColumns');
    const sortBy = formContext.getValues('sortBy');
    const sortOrder = formContext.getValues('sortOrder');
    const parentKey = datasetNameMap[selected[0]?.datasetId] || '';
    const selectedColumnNames = selected.map((c) => c.label);

    return {
      type: 'datagrid',
      columns: {
        [parentKey]: selectedColumnNames,
      },
      defaultSort: sortBy ? [{ field: sortBy, sortDirection: sortOrder }] : undefined,
    } as DatagridElement;
  };

  return (
    <>
      <UnsavedFormDataPrompt
        when={hasUnsavedChanges}
        open={unsavedPromptOpen}
        onCancel={() => setUnsavedPromptOpen(false)}
        onConfirm={() => {
          setUnsavedPromptOpen(false);
          handleClose();
        }}
        navigate={(path) => history.push(path)}
        // Only block navigation if not intentionally bypassed
        shouldBlockNavigation={() => !bypassNavigationRef.current}
        textTranslationKey='unsavedDataDialog'
      />
      <Dialog
        open={open}
        onClose={(event, reason) => {
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
            return;
          }
          handleClose();
        }}
        disableEscapeKeyDown
        TransitionProps={{
          onExited: () => {
            setProgress(Progress.Step1);
            formContext.setValue('selectedColumns', []);
            formContext.setValue('sortBy', '');
            formContext.setValue('sortOrder', 'asc');
            bypassNavigationRef.current = false; // reset bypass after close
          },
        }}
        PaperProps={{
          sx: createDialogSx,
        }}
      >
        {!addAdditionalMode && <LinearProgress variant='determinate' value={progress} sx={linearProgressSx} />}
        <DialogTitle sx={dialogTitleSx}>
          {progress === Progress.Step1 && !addAdditionalMode && t('admin.customReports.createReport')}
          {progress === Progress.Step2 && !addAdditionalMode && t('admin.centralReportsPage.createReport.selectData')}
          {addAdditionalMode && t('admin.centralReportsPage.createReport.addDataTable')}
        </DialogTitle>
        <IconButton
          onClick={() => {
            if (hasUnsavedChanges) {
              setUnsavedPromptOpen(true);
            } else {
              handleClose();
            }
          }}
          sx={closeButtonSx}
          data-testid='create-report-close-button'
        >
          <CloseIcon />
        </IconButton>
        {progress === Progress.Step1 && !addAdditionalMode ? (
          <FormProvider {...formContext}>
            <FormContainer formContext={formContext} onSuccess={() => setProgress(Progress.Step2)}>
              <DialogContent>
                <DialogContentText>{t('admin.centralReportsPage.createReport.content')}</DialogContentText>
                <DialogContentText sx={contentTextSx}>{t('formMessage.requiredExplanation')}</DialogContentText>
                {isAllReportsLoading ? (
                  <CircularProgress sx={loadingSx} aria-label={t('global.loading')} />
                ) : (
                  <Grid container spacing={2} paddingTop={2}>
                    <Grid item xs={12}>
                      <TextField
                        label={t('admin.centralReportsPage.createReport.reportName')}
                        variant='standard'
                        inputProps={{ maxLength: 50, 'data-testid': 'create-report-name-input' }}
                        fullWidth
                        required
                        error={!!formContext.formState.errors.title}
                        helperText={formContext.formState.errors.title?.message}
                        {...register('title')}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        label={t('admin.centralReportsPage.createReport.reportDescription')}
                        variant='standard'
                        inputProps={{ maxLength: 150, 'data-testid': 'create-report-description-input' }}
                        fullWidth
                        {...register('description')}
                      />
                    </Grid>
                  </Grid>
                )}
              </DialogContent>
              <DialogActions sx={dialogActionsSx}>
                <Button
                  onClick={() => {
                    if (hasUnsavedChanges) {
                      setUnsavedPromptOpen(true);
                    } else {
                      handleClose();
                    }
                  }}
                  sx={dialogActionsButtonsSx}
                  variant='text'
                >
                  {t('buttonText.cancel')}
                </Button>
                <Tooltip
                  enterDelay={500}
                  title={
                    !formContext.formState.isValid ? t('admin.centralReportsPage.createReport.disabledTooltip') : ''
                  }
                  disableHoverListener={formContext.formState.isValid}
                  placement='top'
                  arrow
                >
                  <span>
                    <Button
                      type='submit'
                      variant='contained'
                      sx={createButtonSx(theme, isDarkMode)}
                      disabled={!formContext.formState.isValid}
                    >
                      {t('buttonText.next')}
                    </Button>
                  </span>
                </Tooltip>
              </DialogActions>
            </FormContainer>
          </FormProvider>
        ) : (
          <>
            <DialogContent>
              <DialogContentText>{t('admin.centralReportsPage.createReport.selectDataContent')}</DialogContentText>
              <SelectDataset
                selectedOptions={formContext.watch('selectedColumns')}
                setSelectedOptions={(opts) => formContext.setValue('selectedColumns', opts)}
                isEdit={false}
                selectedDatasetTypeId={null}
                hideTabs={false}
              />
              <Typography sx={editMenuTitleSx}>{t('admin.centralReportsPage.createReport.defaultSort')}</Typography>
              <Grid container spacing={3} paddingTop={2}>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth variant='standard' required>
                    <InputLabel id='sort-by-label'>{t('admin.centralReportsPage.createReport.sortBy')}</InputLabel>
                    <Select
                      labelId='sort-by-label'
                      value={
                        formContext.watch('selectedColumns').some((col) => col.label === formContext.watch('sortBy'))
                          ? formContext.watch('sortBy')
                          : ''
                      }
                      onChange={(e) => formContext.setValue('sortBy', e.target.value, { shouldDirty: true })}
                      disabled={formContext.watch('selectedColumns').length === 0}
                      data-testid='sort-by-select'
                    >
                      {formContext.watch('selectedColumns').map((col) => (
                        <MenuItem key={col.id} value={col.label}>
                          {col.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth variant='standard' required>
                    <InputLabel id='sort-order-label'>
                      {t('admin.centralReportsPage.createReport.sortOrder')}
                    </InputLabel>
                    <Select
                      labelId='sort-order-label'
                      value={formContext.watch('sortOrder')}
                      onChange={(e) =>
                        formContext.setValue('sortOrder', e.target.value as 'asc' | 'desc', { shouldDirty: true })
                      }
                      data-testid='sort-order-select'
                    >
                      <MenuItem value='asc'>{t('admin.centralReportsPage.createReport.ascending')}</MenuItem>
                      <MenuItem value='desc'>{t('admin.centralReportsPage.createReport.descending')}</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions sx={dialogActionsSx}>
              <Grid container justifyContent='space-between' alignItems='center'>
                <Grid item>
                  <Button
                    sx={dialogActionsButtonsSx}
                    onClick={() => formContext.setValue('selectedColumns', [])}
                    variant='text'
                  >
                    {t('admin.centralReportsPage.createReport.resetDataSelection')}
                  </Button>
                </Grid>
                <Grid>
                  <Button onClick={() => setProgress(Progress.Step1)} sx={dialogActionsButtonsSx}>
                    {t('buttonText.back')}
                  </Button>
                  <Button
                    variant='contained'
                    sx={createButtonSx(theme, isDarkMode)}
                    disabled={formContext.watch('selectedColumns').length === 0}
                    onClick={() => {
                      bypassNavigationRef.current = true; // allow navigation without prompt

                      if (addAdditionalMode) {
                        const newSection = buildNewSection();
                        handleClose(newSection);
                      } else {
                        const initReport = buildInitReport();
                        sessionStorage.setItem('customReportStateData', JSON.stringify(initReport));
                        history.push({ pathname: routes.CUSTOM_REPORTS });
                        handleClose();
                      }
                    }}
                  >
                    {addAdditionalMode ? t('admin.centralReportsPage.createReport.addTable') : t('buttonText.next')}
                  </Button>
                </Grid>
              </Grid>
            </DialogActions>
          </>
        )}
      </Dialog>
    </>
  );
}

export default CreateReportDialog;
